import { Dropdown, DefinitionTooltip } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import {
  CompletionConfigurationOption,
  CompletionPromptConfigurationOption,
} from 'types/api-responses';
import styles from './document-fields.module.scss';

interface ConfigureProvisionFieldsProps {
  idPrefix?: string;
  extractionConfigurations: CompletionConfigurationOption[];
  completionConfigurations: CompletionConfigurationOption[];
  filteredPrompts: CompletionPromptConfigurationOption[];
  ocrModel?: CompletionConfigurationOption;
  onOcrChange: (model: CompletionConfigurationOption) => void;
  llmModel?: CompletionConfigurationOption;
  onLlmChange: (model: CompletionConfigurationOption) => void;
  promptVersion?: CompletionPromptConfigurationOption;
  onPromptChange: (prompt: CompletionPromptConfigurationOption) => void;
  promptHelperText: string;
  disabled?: boolean;
}

const modelToString = (item: CompletionConfigurationOption | null) => {
  if (!item) return '';
  return `${item.provider} ${item.version}${item.default ? ' (default)' : ''}`;
};

const promptToString = (item: CompletionPromptConfigurationOption | null) => {
  if (!item) return '';
  return `${item.version}${item.default ? ' (default)' : ''}`;
};

// Shared "Configure Provision" section: heading, tooltip, and the OCR / LLM /
// Prompt Version dropdowns. Selection state is owned by the caller; `idPrefix`
// keeps the dropdown ids unique when more than one instance mounts on a page.
export const ConfigureProvisionFields = ({
  idPrefix = '',
  extractionConfigurations,
  completionConfigurations,
  filteredPrompts,
  ocrModel,
  onOcrChange,
  llmModel,
  onLlmChange,
  promptVersion,
  onPromptChange,
  promptHelperText,
  disabled,
}: ConfigureProvisionFieldsProps) => (
  <>
    <h4 className={styles.sectionTitle}>
      Configure Provision
      <DefinitionTooltip
        definition="Adding this document will trigger auto-provisioning. Configure the fields below to customize the output."
        align="bottom-start"
        className={styles.tooltip}
      >
        <Information className={styles.infoIcon} />
      </DefinitionTooltip>
    </h4>
    <fieldset className={styles.fieldset} aria-label="OCR Model Selection">
      <Dropdown
        id={`${idPrefix}ocr-model`}
        titleText="OCR Model"
        label="Select OCR Model"
        itemToString={modelToString}
        items={extractionConfigurations}
        selectedItem={ocrModel}
        onChange={({ selectedItem }) =>
          selectedItem && onOcrChange(selectedItem)
        }
        disabled={disabled}
      />
    </fieldset>
    <fieldset className={styles.fieldset} aria-label="LLM Model Selection">
      <Dropdown
        id={`${idPrefix}llm-model`}
        titleText="LLM Model"
        label="Select LLM Model"
        itemToString={modelToString}
        items={completionConfigurations}
        selectedItem={llmModel}
        onChange={({ selectedItem }) =>
          selectedItem && onLlmChange(selectedItem)
        }
        disabled={disabled}
      />
    </fieldset>
    <fieldset className={styles.fieldset} aria-label="Prompt Version Selection">
      <Dropdown
        id={`${idPrefix}prompt-version`}
        titleText="Prompt Version"
        label="Select Prompt Version"
        helperText={promptHelperText}
        itemToString={promptToString}
        items={filteredPrompts}
        selectedItem={promptVersion}
        onChange={({ selectedItem }) =>
          selectedItem && onPromptChange(selectedItem)
        }
        disabled={disabled}
      />
    </fieldset>
  </>
);
